import { supabase } from "../../../auth/supabaseClient";
import { fetchOrganizationAccess } from "../../../core/settings/organizationAccessService";
import { getEduTeacherSessionToken } from "../../edu/services/studentDeviceService";
import { loadCampusStaffDashboard } from "./staffService";

const CAMPUS_SUBJECTS_TABLE = "campus_subjects";
const CAMPUS_ASSIGNMENTS_TABLE = "campus_assignments";
const CAMPUS_ASSIGNMENT_GRADES_TABLE = "campus_assignment_grades";
const CAMPUS_STUDENT_REPORTS_TABLE = "campus_student_reports";
const CLASSROOM_CLASSES_TABLE = "classroom_classes";
const CLASSROOM_CLASS_STUDENTS_TABLE = "classroom_class_students";
const CLASSROOM_ASSIGNMENTS_TABLE = "classroom_assignments";
const EDU_STUDENT_DEVICE_STUDENTS_TABLE = "edu_student_device_students";

export const ACADEMIC_QUARTERS = ["Q1", "Q2", "Q3", "Q4"];

function normalizeArray(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function normalizeText(value) {
  return String(value || "").trim().toLowerCase();
}

function normalizeDisplayText(value) {
  return String(value || "").trim();
}

function isMissingRelationError(error) {
  return (
    error?.code === "42P01" ||
    error?.code === "42703" ||
    error?.code === "PGRST204" ||
    error?.code === "PGRST205" ||
    (typeof error?.message === "string" &&
      error.message.toLowerCase().includes("does not exist"))
  );
}

function slugifyCode(value = "") {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 16);
}

function isAssignmentTargetedToStudent(assignment = {}, student = {}) {
  const assignedCampusStudentIds = normalizeArray(assignment.assignedStudentIds).map((value) => String(value));
  const assignedGradeLevels = normalizeArray(assignment.assignedGradeLevels).map(normalizeText);
  const campusStudentId = student.campus_student_id ? String(student.campus_student_id) : "";
  const gradeLevel = normalizeText(student.grade_level);

  if (assignedCampusStudentIds.length > 0) {
    return campusStudentId && assignedCampusStudentIds.includes(campusStudentId);
  }

  if (assignedGradeLevels.length > 0) {
    return assignedGradeLevels.includes(gradeLevel);
  }

  return true;
}

async function syncClassroomStudentsForAssignment({
  assignment,
  accountId,
  classroomClassId,
}) {
  const { data: studentRows, error: studentError } = await supabase
    .from(EDU_STUDENT_DEVICE_STUDENTS_TABLE)
    .select("id, campus_student_id, grade_level, is_active")
    .eq("account_id", accountId)
    .eq("is_active", true);

  if (studentError) {
    if (isMissingRelationError(studentError)) {
      throw new Error("Student device tables are not installed yet. Run `sql/edu-student-device.sql` in Supabase first.");
    }

    console.warn("Oikos Classroom roster sync skipped:", studentError);
    return;
  }

  const matchingStudents = (studentRows || []).filter((student) =>
    isAssignmentTargetedToStudent(assignment, student)
  );

  if (matchingStudents.length === 0) {
    return;
  }

  const { error: rosterError } = await supabase
    .from(CLASSROOM_CLASS_STUDENTS_TABLE)
    .upsert(
      matchingStudents.map((student) => ({
        class_id: classroomClassId,
        student_id: student.id,
        campus_student_id: student.campus_student_id || null,
      })),
      { onConflict: "class_id,student_id" }
    );

  if (rosterError) {
    console.warn("Oikos Classroom roster sync skipped:", rosterError);
  }
}

function normalizeSubject(row = {}) {
  return {
    id: row.id || crypto.randomUUID(),
    accountId: row.account_id || "",
    teacherUserId: row.teacher_user_id || "",
    teacherStaffId: row.teacher_staff_id || "",
    name: row.name || "",
    code: row.code || "",
    description: row.description || "",
    gradeLevels: normalizeArray(row.grade_levels),
    isActive: row.is_active !== false,
    createdAt: row.created_at || "",
    updatedAt: row.updated_at || "",
  };
}

function normalizeAssignment(row = {}) {
  return {
    id: row.id || crypto.randomUUID(),
    accountId: row.account_id || "",
    subjectId: row.subject_id || "",
    teacherUserId: row.teacher_user_id || "",
    teacherStaffId: row.teacher_staff_id || "",
    title: row.title || "",
    description: row.description || "",
    category: row.category || "Assignment",
    academicQuarter: row.academic_quarter || "Q1",
    dueDate: row.due_date || "",
    pointsPossible: Number(row.points_possible || 0) || 0,
    assignedStudentIds: normalizeArray(row.assigned_student_ids).map((value) => String(value)),
    assignedGradeLevels: normalizeArray(row.assigned_grade_levels),
    sendToOikosClassroom: row.send_to_oikos_classroom === true,
    oikosClassroomAssignmentId: row.oikos_classroom_assignment_id || "",
    status: row.status || "draft",
    createdAt: row.created_at || "",
    updatedAt: row.updated_at || "",
  };
}

function normalizeSessionWorkspace(data = {}) {
  return {
    account: data.account || null,
    membership: data.membership || null,
    isOwner: data.isOwner === true,
    hasPortalAccess: data.hasPortalAccess !== false,
    teacher: data.teacher || null,
    students: Array.isArray(data.students) ? data.students : [],
    subjects: Array.isArray(data.subjects) ? data.subjects : [],
    assignments: Array.isArray(data.assignments) ? data.assignments : [],
    grades: Array.isArray(data.grades) ? data.grades : [],
    reports: Array.isArray(data.reports) ? data.reports : [],
    allSubjects: Array.isArray(data.allSubjects) ? data.allSubjects : data.subjects || [],
    allAssignments: Array.isArray(data.allAssignments) ? data.allAssignments : data.assignments || [],
    allGrades: Array.isArray(data.allGrades) ? data.allGrades : [],
    allReports: Array.isArray(data.allReports) ? data.allReports : [],
    schemaReady: data.schemaReady !== false,
    sessionMode: data.sessionMode || "eduTeacher",
    teacherSessionToken: getEduTeacherSessionToken(),
  };
}

export async function loadTeacherPortalWorkspaceFromSession() {
  const teacherSessionToken = getEduTeacherSessionToken();
  if (!teacherSessionToken) {
    throw new Error("Teacher login is required.");
  }

  const { data, error } = await supabase.rpc("campus_teacher_portal_workspace", {
    p_teacher_session_token: teacherSessionToken,
  });

  if (error) throw error;
  return normalizeSessionWorkspace(data || {});
}

async function syncTeacherPortalAssignmentToClassroom({
  assignment,
  accountId,
  teacherUserId,
  teacherEduTeacherId = "",
}) {
  const { data: subjectRow, error: subjectError } = await supabase
    .from(CAMPUS_SUBJECTS_TABLE)
    .select("id, name, code, description")
    .eq("id", assignment.subjectId)
    .eq("account_id", accountId)
    .maybeSingle();

  if (subjectError) throw subjectError;

  const { data: existingClass, error: classLookupError } = await supabase
    .from(CLASSROOM_CLASSES_TABLE)
    .select("id")
    .eq("account_id", accountId)
    .eq("campus_subject_id", assignment.subjectId)
    .maybeSingle();

  if (classLookupError) {
    if (isMissingRelationError(classLookupError)) {
      throw new Error("Oikos Classroom tables are not installed yet. Run `sql/classroom.sql` in Supabase first.");
    }

    throw classLookupError;
  }

  let classroomClassId = existingClass?.id || "";
  if (!classroomClassId) {
    const { data: classRow, error: classCreateError } = await supabase
      .from(CLASSROOM_CLASSES_TABLE)
      .insert({
        account_id: accountId,
        campus_subject_id: assignment.subjectId,
        teacher_id: teacherEduTeacherId || null,
        teacher_user_id: teacherUserId,
        name: subjectRow?.name || assignment.title || "Class",
        subject: subjectRow?.name || "",
        source: "campus",
      })
      .select("id")
      .single();

    if (classCreateError) throw classCreateError;
    classroomClassId = classRow.id;
  }

  const classroomPayload = {
    account_id: accountId,
    class_id: classroomClassId,
    campus_assignment_id: assignment.id,
    teacher_id: teacherEduTeacherId || null,
    teacher_user_id: teacherUserId,
    title: assignment.title,
    instructions: assignment.description || "",
    due_at: assignment.dueDate || null,
    points_possible: Number(assignment.pointsPossible || 0) || 0,
    status: assignment.status || "published",
    source: "campus",
  };

  const { data: existingAssignment, error: assignmentLookupError } = await supabase
    .from(CLASSROOM_ASSIGNMENTS_TABLE)
    .select("id")
    .eq("account_id", accountId)
    .eq("campus_assignment_id", assignment.id)
    .maybeSingle();

  if (assignmentLookupError) throw assignmentLookupError;

  const assignmentQuery = existingAssignment?.id
    ? supabase
        .from(CLASSROOM_ASSIGNMENTS_TABLE)
        .update(classroomPayload)
        .eq("id", existingAssignment.id)
        .eq("account_id", accountId)
    : supabase.from(CLASSROOM_ASSIGNMENTS_TABLE).insert(classroomPayload);

  const { data: classroomAssignment, error: classroomAssignmentError } = await assignmentQuery
    .select("id")
    .single();

  if (classroomAssignmentError) throw classroomAssignmentError;

  await syncClassroomStudentsForAssignment({
    assignment,
    accountId,
    classroomClassId,
  });

  const { data: updatedCampusAssignment, error: campusUpdateError } = await supabase
    .from(CAMPUS_ASSIGNMENTS_TABLE)
    .update({
      send_to_oikos_classroom: true,
      oikos_classroom_assignment_id: classroomAssignment.id,
    })
    .eq("id", assignment.id)
    .eq("account_id", accountId)
    .select("*")
    .single();

  if (campusUpdateError) throw campusUpdateError;

  return normalizeAssignment(updatedCampusAssignment);
}

function normalizeGrade(row = {}) {
  return {
    id: row.id || crypto.randomUUID(),
    accountId: row.account_id || "",
    assignmentId: row.assignment_id || "",
    studentId: row.student_id || "",
    teacherUserId: row.teacher_user_id || "",
    score: row.score === null || row.score === undefined || row.score === ""
      ? ""
      : Number(row.score),
    feedback: row.feedback || "",
    status: row.status || "draft",
    gradedAt: row.graded_at || "",
    createdAt: row.created_at || "",
    updatedAt: row.updated_at || "",
  };
}

function normalizeReport(row = {}) {
  return {
    id: row.id || crypto.randomUUID(),
    accountId: row.account_id || "",
    studentId: row.student_id || "",
    teacherUserId: row.teacher_user_id || "",
    teacherStaffId: row.teacher_staff_id || "",
    academicQuarter: row.academic_quarter || "Q1",
    title: row.title || "",
    summary: row.summary || "",
    academicProgress: row.academic_progress || "",
    strengths: row.strengths || "",
    growthAreas: row.growth_areas || "",
    familySupport: row.family_support || "",
    conduct: row.conduct || "",
    attendance: row.attendance || "",
    gradeSnapshot: Array.isArray(row.grade_snapshot) ? row.grade_snapshot : [],
    metadata: row.metadata || {},
    status: row.status || "draft",
    generatedAt: row.generated_at || "",
    createdAt: row.created_at || "",
    updatedAt: row.updated_at || "",
  };
}

function getStudentSubjectTeacherAssignments(student = {}) {
  return Array.isArray(student?.customFields?.subjectTeachers)
    ? student.customFields.subjectTeachers
    : [];
}

function matchTeacherStaff(staffRows = [], user) {
  const userId = String(user?.id || "");
  const userEmail = normalizeText(user?.email);
  const userFullName = normalizeText(user?.user_metadata?.full_name);
  const emailName = userEmail.includes("@") ? userEmail.split("@")[0] : "";

  return (
    staffRows.find((item) => item.linkedUserId && item.linkedUserId === userId) ||
    staffRows.find(
      (item) => item.email && normalizeText(item.email) === userEmail
    ) ||
    staffRows.find(
      (item) => item.displayName && normalizeText(item.displayName) === userFullName
    ) ||
    staffRows.find(
      (item) =>
        item.displayName &&
        emailName &&
        normalizeText(item.displayName).replace(/\s+/g, "") === emailName.replace(/\s+/g, "")
    ) ||
    null
  );
}

function deriveTeacherStudents(teacher, allStudents = []) {
  if (!teacher) {
    return [];
  }

  const directStudentIds = new Set(
    normalizeArray(teacher.studentAssignments).map((value) => String(value))
  );
  const teacherName = normalizeText(teacher.displayName);
  const merged = [];
  const seenStudentIds = new Set();

  allStudents.forEach((student) => {
    const studentId = String(student.id || "");
    const matchesDirect = studentId && directStudentIds.has(studentId);
    const matchesTeacherName =
      teacherName && normalizeText(student.homeroomTeacher) === teacherName;
    const matchesSubjectTeacher = getStudentSubjectTeacherAssignments(student).some((assignment) => {
      const assignmentTeacherStaffId = String(assignment?.teacherStaffId || "");
      const assignmentTeacherUserId = String(assignment?.teacherUserId || "");
      const assignmentTeacherName = normalizeText(assignment?.teacherDisplayName);

      return (
        (teacher?.id && assignmentTeacherStaffId === String(teacher.id)) ||
        (teacher?.linkedUserId && assignmentTeacherUserId === String(teacher.linkedUserId)) ||
        (teacherName && assignmentTeacherName === teacherName)
      );
    });

    if (!matchesDirect && !matchesTeacherName && !matchesSubjectTeacher) {
      return;
    }

    if (seenStudentIds.has(studentId)) {
      return;
    }

    seenStudentIds.add(studentId);
    merged.push(student);
  });

  return merged.sort((a, b) =>
    String(a.displayName || "").localeCompare(String(b.displayName || ""))
  );
}

async function loadTeacherPortalTables(accountId, userId) {
  try {
    const [
      { data: subjectRows, error: subjectError },
      { data: assignmentRows, error: assignmentError },
      { data: gradeRows, error: gradeError },
      { data: reportRows, error: reportError },
    ] =
      await Promise.all([
        supabase
          .from(CAMPUS_SUBJECTS_TABLE)
          .select("*")
          .eq("account_id", accountId)
          .eq("is_active", true)
          .order("name", { ascending: true }),
        supabase
          .from(CAMPUS_ASSIGNMENTS_TABLE)
          .select("*")
          .eq("account_id", accountId)
          .order("due_date", { ascending: true, nullsFirst: false })
          .order("created_at", { ascending: false }),
        supabase
          .from(CAMPUS_ASSIGNMENT_GRADES_TABLE)
          .select("*")
          .eq("account_id", accountId),
        supabase
          .from(CAMPUS_STUDENT_REPORTS_TABLE)
          .select("*")
          .eq("account_id", accountId)
          .order("academic_quarter", { ascending: true })
          .order("updated_at", { ascending: false }),
      ]);

    if (subjectError) throw subjectError;
    if (assignmentError) throw assignmentError;
    if (gradeError) throw gradeError;
    if (reportError) throw reportError;

    const allSubjects = Array.isArray(subjectRows) ? subjectRows.map((row) => normalizeSubject(row)) : [];
    const allAssignments = Array.isArray(assignmentRows) ? assignmentRows.map((row) => normalizeAssignment(row)) : [];
    const allGrades = Array.isArray(gradeRows) ? gradeRows.map((row) => normalizeGrade(row)) : [];
    const allReports = Array.isArray(reportRows) ? reportRows.map((row) => normalizeReport(row)) : [];

    return {
      schemaReady: true,
      allSubjects,
      allAssignments,
      allGrades,
      allReports,
      subjects: allSubjects.filter((row) => row.teacherUserId === userId),
      assignments: allAssignments.filter((row) => row.teacherUserId === userId),
      grades: allGrades.filter((row) => row.teacherUserId === userId),
      reports: allReports.filter((row) => row.teacherUserId === userId),
    };
  } catch (error) {
    if (isMissingRelationError(error)) {
      return {
        schemaReady: false,
        allSubjects: [],
        allAssignments: [],
        allGrades: [],
        allReports: [],
        subjects: [],
        assignments: [],
        grades: [],
        reports: [],
      };
    }

    throw error;
  }
}

export async function loadTeacherPortalWorkspace(user) {
  if (!user?.id) {
    return {
      account: null,
      membership: null,
      isOwner: false,
      hasPortalAccess: false,
      teacher: null,
      students: [],
      subjects: [],
      assignments: [],
      grades: [],
      reports: [],
      allSubjects: [],
      allAssignments: [],
      allGrades: [],
      allReports: [],
      schemaReady: true,
    };
  }

  const access = await fetchOrganizationAccess(user.id, "campus");
  const account = access?.account || null;

  if (!account?.id) {
    return {
      account: null,
      membership: access?.membership || null,
      isOwner: false,
      hasPortalAccess: false,
      teacher: null,
      students: [],
      subjects: [],
      assignments: [],
      grades: [],
      reports: [],
      allSubjects: [],
      allAssignments: [],
      allGrades: [],
      allReports: [],
      schemaReady: true,
    };
  }

  const dashboard = await loadCampusStaffDashboard(user.id);
  const teacher = matchTeacherStaff(dashboard.staff || [], user);
  const isOwner = access?.isOwner === true || account.owner_user_id === user.id;
  const hasPortalAccess = isOwner || teacher?.teacherPortalAccess === true;
  const tableState = await loadTeacherPortalTables(account.id, user.id);
  const teacherStudents = deriveTeacherStudents(teacher, dashboard.students || []);

  return {
    account,
    membership: access?.membership || null,
    isOwner,
    hasPortalAccess,
    teacher,
    students: teacherStudents,
    subjects: tableState.subjects,
    assignments: tableState.assignments,
    grades: tableState.grades,
    reports: tableState.reports,
    allSubjects: tableState.allSubjects,
    allAssignments: tableState.allAssignments,
    allGrades: tableState.allGrades,
    allReports: tableState.allReports,
    schemaReady: tableState.schemaReady,
  };
}

function emptyStudentAcademicRecords(schemaReady = true) {
  return {
    schemaReady,
    subjects: [],
    assignments: [],
    grades: [],
    reports: [],
  };
}

export async function loadCampusStudentAcademicRecords(userId, studentId) {
  if (!userId || !studentId) {
    return emptyStudentAcademicRecords();
  }

  const access = await fetchOrganizationAccess(userId, "campus");
  const accountId = access?.account?.id || "";

  if (!accountId) {
    return emptyStudentAcademicRecords();
  }

  try {
    const [
      { data: subjectRows, error: subjectError },
      { data: assignmentRows, error: assignmentError },
      { data: gradeRows, error: gradeError },
      { data: reportRows, error: reportError },
      { data: staffRows, error: staffError },
    ] = await Promise.all([
      supabase
        .from(CAMPUS_SUBJECTS_TABLE)
        .select("*")
        .eq("account_id", accountId)
        .eq("is_active", true),
      supabase
        .from(CAMPUS_ASSIGNMENTS_TABLE)
        .select("*")
        .eq("account_id", accountId)
        .order("due_date", { ascending: false, nullsFirst: false })
        .order("created_at", { ascending: false }),
      supabase
        .from(CAMPUS_ASSIGNMENT_GRADES_TABLE)
        .select("*")
        .eq("account_id", accountId)
        .eq("student_id", studentId)
        .order("graded_at", { ascending: false, nullsFirst: false }),
      supabase
        .from(CAMPUS_STUDENT_REPORTS_TABLE)
        .select("*")
        .eq("account_id", accountId)
        .eq("student_id", studentId)
        .order("academic_quarter", { ascending: true })
        .order("updated_at", { ascending: false }),
      supabase
        .from("campus_staff")
        .select("id, display_name, linked_user_id, email")
        .eq("account_id", accountId),
    ]);

    if (subjectError) throw subjectError;
    if (assignmentError) throw assignmentError;
    if (gradeError) throw gradeError;
    if (reportError) throw reportError;
    if (staffError && !isMissingRelationError(staffError)) throw staffError;

    const subjects = Array.isArray(subjectRows) ? subjectRows.map((row) => normalizeSubject(row)) : [];
    const assignments = (Array.isArray(assignmentRows) ? assignmentRows.map((row) => normalizeAssignment(row)) : [])
      .filter((assignment) => (assignment.assignedStudentIds || []).includes(String(studentId)));
    const assignmentIds = new Set(assignments.map((assignment) => assignment.id));
    const subjectById = new Map(subjects.map((subject) => [subject.id, subject]));
    const assignmentById = new Map(assignments.map((assignment) => [assignment.id, assignment]));
    const staffById = new Map((staffRows || []).map((staff) => [staff.id, staff]));
    const staffByUserId = new Map(
      (staffRows || [])
        .filter((staff) => staff.linked_user_id)
        .map((staff) => [staff.linked_user_id, staff])
    );

    const grades = (Array.isArray(gradeRows) ? gradeRows.map((row) => normalizeGrade(row)) : [])
      .filter((grade) => assignmentIds.has(grade.assignmentId))
      .map((grade) => {
        const assignment = assignmentById.get(grade.assignmentId) || {};
        const subject = subjectById.get(assignment.subjectId) || {};
        const staff = staffById.get(assignment.teacherStaffId) || staffByUserId.get(grade.teacherUserId) || {};

        return {
          ...grade,
          assignmentTitle: assignment.title || "Assignment",
          assignmentCategory: assignment.category || "Assignment",
          academicQuarter: assignment.academicQuarter || "Q1",
          dueDate: assignment.dueDate || "",
          pointsPossible: assignment.pointsPossible || 0,
          subjectId: assignment.subjectId || "",
          subjectName: subject.name || "Subject",
          teacherName: staff.display_name || subject.name || "Teacher",
        };
      });

    const reports = (Array.isArray(reportRows) ? reportRows.map((row) => normalizeReport(row)) : [])
      .map((report) => {
        const staff = staffById.get(report.teacherStaffId) || staffByUserId.get(report.teacherUserId) || {};

        return {
          ...report,
          teacherName: staff.display_name || "Teacher",
        };
      });

    return {
      schemaReady: true,
      subjects,
      assignments,
      grades,
      reports,
    };
  } catch (error) {
    if (isMissingRelationError(error)) {
      return emptyStudentAcademicRecords(false);
    }

    throw error;
  }
}

export async function createTeacherPortalSubject({
  accountId,
  teacherUserId,
  teacherStaffId = "",
  teacherSessionToken = "",
  name,
  code,
  description = "",
  gradeLevels = [],
}) {
  const trimmedName = String(name || "").trim();

  if (teacherSessionToken) {
    if (!trimmedName) {
      throw new Error("Subject name is required.");
    }

    const { data, error } = await supabase.rpc("campus_teacher_portal_save_subject", {
      p_teacher_session_token: teacherSessionToken,
      p_subject_id: null,
      p_name: trimmedName,
      p_code: slugifyCode(code || trimmedName),
      p_description: String(description || "").trim(),
      p_grade_levels: normalizeArray(gradeLevels),
    });

    if (error) throw error;
    return data;
  }

  if (!accountId || !teacherUserId || !trimmedName) {
    throw new Error("Subject name is required.");
  }

  const payload = {
    account_id: accountId,
    teacher_user_id: teacherUserId,
    teacher_staff_id: teacherStaffId || null,
    name: trimmedName,
    code: slugifyCode(code || trimmedName),
    description: String(description || "").trim(),
    grade_levels: normalizeArray(gradeLevels),
    is_active: true,
  };

  const { data, error } = await supabase
    .from(CAMPUS_SUBJECTS_TABLE)
    .insert(payload)
    .select("*")
    .single();

  if (error) {
    if (isMissingRelationError(error)) {
      throw new Error("Teacher portal tables are not installed yet. Run `sql/campus-teacher-portal.sql` in Supabase first.");
    }

    throw error;
  }

  return normalizeSubject(data);
}

export async function createTeacherPortalAssignment({
  accountId,
  teacherUserId,
  teacherStaffId = "",
  teacherEduTeacherId = "",
  teacherSessionToken = "",
  subjectId,
  title,
  description = "",
  category = "Assignment",
  academicQuarter = "Q1",
  dueDate = "",
  pointsPossible = 100,
  assignedStudentIds = [],
  assignedGradeLevels = [],
  sendToOikosClassroom = false,
  status = "published",
}) {
  const trimmedTitle = String(title || "").trim();

  if (teacherSessionToken) {
    if (!subjectId || !trimmedTitle) {
      throw new Error("Subject and assignment title are required.");
    }

    const { data, error } = await supabase.rpc("campus_teacher_portal_save_assignment", {
      p_teacher_session_token: teacherSessionToken,
      p_assignment_id: null,
      p_subject_id: subjectId,
      p_title: trimmedTitle,
      p_description: String(description || "").trim(),
      p_category: String(category || "Assignment").trim() || "Assignment",
      p_academic_quarter: ACADEMIC_QUARTERS.includes(String(academicQuarter || "").trim())
        ? String(academicQuarter).trim()
        : "Q1",
      p_due_date: dueDate || null,
      p_points_possible: Number(pointsPossible || 0) || 0,
      p_assigned_student_ids: normalizeArray(assignedStudentIds).map((value) => String(value)),
      p_assigned_grade_levels: normalizeArray(assignedGradeLevels),
      p_send_to_oikos_classroom: Boolean(sendToOikosClassroom),
      p_status: String(status || "published").trim() || "published",
    });

    if (error) throw error;
    return data;
  }

  if (!accountId || !teacherUserId || !subjectId || !trimmedTitle) {
    throw new Error("Subject and assignment title are required.");
  }

  const payload = {
    account_id: accountId,
    subject_id: subjectId,
    teacher_user_id: teacherUserId,
    teacher_staff_id: teacherStaffId || null,
    title: trimmedTitle,
    description: String(description || "").trim(),
    category: String(category || "Assignment").trim() || "Assignment",
    academic_quarter: ACADEMIC_QUARTERS.includes(String(academicQuarter || "").trim())
      ? String(academicQuarter).trim()
      : "Q1",
    due_date: dueDate || null,
    points_possible: Number(pointsPossible || 0) || 0,
    assigned_student_ids: normalizeArray(assignedStudentIds).map((value) => String(value)),
    assigned_grade_levels: normalizeArray(assignedGradeLevels),
    send_to_oikos_classroom: Boolean(sendToOikosClassroom),
    status: String(status || "published").trim() || "published",
  };

  const { data, error } = await supabase
    .from(CAMPUS_ASSIGNMENTS_TABLE)
    .insert(payload)
    .select("*")
    .single();

  if (error) {
    if (isMissingRelationError(error)) {
      throw new Error("Teacher portal tables are not installed yet. Run `sql/campus-teacher-portal.sql` in Supabase first.");
    }

    throw error;
  }

  const assignment = normalizeAssignment(data);

  if (sendToOikosClassroom) {
    return syncTeacherPortalAssignmentToClassroom({
      assignment,
      accountId,
      teacherUserId,
      teacherEduTeacherId,
    });
  }

  return assignment;
}

export async function updateTeacherPortalAssignment({
  assignmentId,
  accountId,
  teacherUserId,
  teacherStaffId = "",
  teacherEduTeacherId = "",
  teacherSessionToken = "",
  subjectId,
  title,
  description = "",
  category = "Assignment",
  academicQuarter = "Q1",
  dueDate = "",
  pointsPossible = 100,
  assignedStudentIds = [],
  assignedGradeLevels = [],
  sendToOikosClassroom = false,
  status = "published",
}) {
  if (teacherSessionToken) {
    if (!assignmentId || !subjectId || !String(title || "").trim()) {
      throw new Error("Subject and assignment title are required.");
    }

    const { data, error } = await supabase.rpc("campus_teacher_portal_save_assignment", {
      p_teacher_session_token: teacherSessionToken,
      p_assignment_id: assignmentId,
      p_subject_id: subjectId,
      p_title: String(title || "").trim(),
      p_description: String(description || "").trim(),
      p_category: String(category || "Assignment").trim() || "Assignment",
      p_academic_quarter: ACADEMIC_QUARTERS.includes(String(academicQuarter || "").trim())
        ? String(academicQuarter).trim()
        : "Q1",
      p_due_date: dueDate || null,
      p_points_possible: Number(pointsPossible || 0) || 0,
      p_assigned_student_ids: normalizeArray(assignedStudentIds).map((value) => String(value)),
      p_assigned_grade_levels: normalizeArray(assignedGradeLevels),
      p_send_to_oikos_classroom: Boolean(sendToOikosClassroom),
      p_status: String(status || "published").trim() || "published",
    });

    if (error) throw error;
    return data;
  }

  if (!assignmentId || !accountId || !teacherUserId || !subjectId || !String(title || "").trim()) {
    throw new Error("Subject and assignment title are required.");
  }

  const payload = {
    subject_id: subjectId,
    teacher_user_id: teacherUserId,
    teacher_staff_id: teacherStaffId || null,
    title: String(title || "").trim(),
    description: String(description || "").trim(),
    category: String(category || "Assignment").trim() || "Assignment",
    academic_quarter: ACADEMIC_QUARTERS.includes(String(academicQuarter || "").trim())
      ? String(academicQuarter).trim()
      : "Q1",
    due_date: dueDate || null,
    points_possible: Number(pointsPossible || 0) || 0,
    assigned_student_ids: normalizeArray(assignedStudentIds).map((value) => String(value)),
    assigned_grade_levels: normalizeArray(assignedGradeLevels),
    send_to_oikos_classroom: Boolean(sendToOikosClassroom),
    status: String(status || "published").trim() || "published",
  };

  const { data, error } = await supabase
    .from(CAMPUS_ASSIGNMENTS_TABLE)
    .update(payload)
    .eq("id", assignmentId)
    .eq("account_id", accountId)
    .eq("teacher_user_id", teacherUserId)
    .select("*")
    .single();

  if (error) {
    if (isMissingRelationError(error)) {
      throw new Error("Teacher portal tables are not installed yet. Run `sql/campus-teacher-portal.sql` in Supabase first.");
    }

    throw error;
  }

  const assignment = normalizeAssignment(data);

  if (sendToOikosClassroom) {
    return syncTeacherPortalAssignmentToClassroom({
      assignment,
      accountId,
      teacherUserId,
      teacherEduTeacherId,
    });
  }

  return assignment;
}

export async function deleteTeacherPortalAssignment({ assignmentId, accountId, teacherUserId }) {
  if (!assignmentId || !accountId || !teacherUserId) {
    throw new Error("Assignment is required.");
  }

  const { error } = await supabase
    .from(CAMPUS_ASSIGNMENTS_TABLE)
    .delete()
    .eq("id", assignmentId)
    .eq("account_id", accountId)
    .eq("teacher_user_id", teacherUserId);

  if (error) {
    if (isMissingRelationError(error)) {
      throw new Error("Teacher portal tables are not installed yet. Run `sql/campus-teacher-portal.sql` in Supabase first.");
    }

    throw error;
  }

  return true;
}

export async function saveTeacherPortalGrade({
  accountId,
  assignmentId,
  studentId,
  teacherUserId,
  score,
  feedback = "",
  status = "graded",
}) {
  if (!accountId || !assignmentId || !studentId || !teacherUserId) {
    throw new Error("Assignment, student, and teacher are required.");
  }

  const normalizedScore =
    score === "" || score === null || score === undefined
      ? null
      : Number(score);

  const payload = {
    account_id: accountId,
    assignment_id: assignmentId,
    student_id: studentId,
    teacher_user_id: teacherUserId,
    score: Number.isFinite(normalizedScore) ? normalizedScore : null,
    feedback: String(feedback || "").trim(),
    status: String(status || "graded").trim() || "graded",
    graded_at: new Date().toISOString(),
  };

  const { data, error } = await supabase
    .from(CAMPUS_ASSIGNMENT_GRADES_TABLE)
    .upsert(payload, {
      onConflict: "account_id,assignment_id,student_id",
    })
    .select("*")
    .single();

  if (error) {
    if (isMissingRelationError(error)) {
      throw new Error("Teacher portal tables are not installed yet. Run `sql/campus-teacher-portal.sql` in Supabase first.");
    }

    throw error;
  }

  return normalizeGrade(data);
}

export async function saveTeacherPortalReport({
  accountId,
  studentId,
  teacherUserId,
  teacherStaffId = "",
  academicQuarter = "Q1",
  title = "",
  summary = "",
  academicProgress = "",
  strengths = "",
  growthAreas = "",
  familySupport = "",
  conduct = "",
  attendance = "",
  gradeSnapshot = [],
  metadata = {},
  status = "draft",
}) {
  if (!accountId || !studentId || !teacherUserId) {
    throw new Error("Student and teacher are required.");
  }

  const quarter = ACADEMIC_QUARTERS.includes(String(academicQuarter || "").trim())
    ? String(academicQuarter).trim()
    : "Q1";

  const payload = {
    account_id: accountId,
    student_id: studentId,
    teacher_user_id: teacherUserId,
    teacher_staff_id: teacherStaffId || null,
    academic_quarter: quarter,
    title: String(title || "").trim(),
    summary: String(summary || "").trim(),
    academic_progress: String(academicProgress || "").trim(),
    strengths: String(strengths || "").trim(),
    growth_areas: String(growthAreas || "").trim(),
    family_support: String(familySupport || "").trim(),
    conduct: String(conduct || "").trim(),
    attendance: String(attendance || "").trim(),
    grade_snapshot: Array.isArray(gradeSnapshot) ? gradeSnapshot : [],
    metadata: metadata && typeof metadata === "object" ? metadata : {},
    status: String(status || "draft").trim() || "draft",
    generated_at: new Date().toISOString(),
  };

  const { data, error } = await supabase
    .from(CAMPUS_STUDENT_REPORTS_TABLE)
    .upsert(payload, {
      onConflict: "account_id,student_id,teacher_user_id,academic_quarter",
    })
    .select("*")
    .single();

  if (error) {
    if (isMissingRelationError(error)) {
      throw new Error("Teacher portal report tables are not installed yet. Run `sql/campus-teacher-portal.sql` in Supabase first.");
    }

    throw error;
  }

  return normalizeReport(data);
}
